/**
 * Manual transaction entry (FE-3, PHASE1 §4 `POST /api/transactions`). Fields:
 * account + date + amount (signed) + optional payee/purpose + optional bucket.
 * A bucket → one user split, review_status=reviewed; no bucket → the booking
 * lands in the inbox (review_status=pending). Invalidates `['transactions']` +
 * `['inbox']`.
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current props each time (no reset effect).
 */
import { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { BucketSelect } from '../../components/ui/BucketSelect.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { parseAmountInput } from '../../lib/format.ts';
import { useAccounts } from '../../lib/hooks/useAccounts.ts';
import { useCreateManualTransaction } from '../../lib/hooks/useAccountMutations.ts';
import type { AccountOut } from '../../lib/types.ts';

function todayIso(): string {
  return new Date().toLocaleDateString('sv-SE');
}

function presetAccount(
  accounts: AccountOut[],
  defaultAccountId: number | null,
): string | undefined {
  // An explicit request that cannot be honoured must NOT fall through to the
  // "only one account left" shortcut: `accounts` excludes closed accounts, so a
  // card that asked for a closed one would otherwise silently preselect a
  // DIFFERENT account and book against it.
  if (defaultAccountId != null) {
    return accounts.some((a) => a.id === defaultAccountId) ? String(defaultAccountId) : undefined;
  }
  return accounts.length === 1 ? String(accounts[0].id) : undefined;
}

export interface ManualTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Preselect this account when opened from an account card. */
  defaultAccountId?: number | null;
}

export function ManualTransactionDialog({
  open,
  onOpenChange,
  defaultAccountId,
}: ManualTransactionDialogProps) {
  if (!open) return null;
  return (
    <ManualTransactionForm
      defaultAccountId={defaultAccountId ?? null}
      onClose={() => onOpenChange(false)}
    />
  );
}

function ManualTransactionForm({
  defaultAccountId,
  onClose,
}: {
  defaultAccountId: number | null;
  onClose: () => void;
}) {
  const accounts = useAccounts();
  const create = useCreateManualTransaction();

  const accountOptions = useMemo<SelectOption[]>(
    () => accounts.map((a) => ({ value: String(a.id), label: a.name })),
    [accounts],
  );

  const [accountId, setAccountId] = useState<string | undefined>(() =>
    presetAccount(accounts, defaultAccountId),
  );
  const [date, setDate] = useState(() => todayIso());
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [purpose, setPurpose] = useState('');
  const [bucketId, setBucketId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    setError(null);
    if (!accountId) {
      setError('Bitte ein Konto wählen.');
      return;
    }
    if (!date) {
      setError('Bitte ein Buchungsdatum angeben.');
      return;
    }
    const value = parseAmountInput(amount);
    if (value === null) {
      setError('Betrag ist keine gültige Zahl (z. B. -49,90 oder 1200,00).');
      return;
    }
    const trimmedPayee = payee.trim();
    const trimmedPurpose = purpose.trim();

    create.mutate(
      {
        account_id: Number(accountId),
        booking_date: date,
        amount: value,
        counterparty_name: trimmedPayee === '' ? null : trimmedPayee,
        purpose: trimmedPurpose === '' ? null : trimmedPurpose,
        bucket_id: bucketId,
      },
      {
        onSuccess: onClose,
        onError: (err) =>
          setError(
            err instanceof ApiRequestError
              ? err.message
              : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
          ),
      },
    );
  }

  const noAccounts = accounts.length === 0;

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Buchung erfassen"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending || noAccounts}>
            Buchung speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />
        {noAccounts ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            Es ist noch kein Konto vorhanden. Bitte zuerst ein Konto anlegen.
          </p>
        ) : null}

        <Field label="Konto" required>
          {(id) => (
            <Select
              id={id}
              value={accountId}
              onValueChange={setAccountId}
              options={accountOptions}
              placeholder="Konto wählen …"
            />
          )}
        </Field>

        <Field label="Buchungsdatum" required>
          {(id) => (
            <Input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          )}
        </Field>

        <Field label="Betrag" required hint="Negativ = Ausgabe, positiv = Einnahme (z. B. -49,90).">
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
            />
          )}
        </Field>

        <Field label="Empfänger / Auftraggeber">
          {(id) => (
            <Input
              id={id}
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              placeholder="Optional"
            />
          )}
        </Field>

        <Field label="Verwendungszweck">
          {(id) => (
            <Input
              id={id}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Optional"
            />
          )}
        </Field>

        <Field label="Kategorie" hint="Optional. Ohne Kategorie landet die Buchung in der Inbox.">
          {(id) => <BucketSelect id={id} value={bucketId} onChange={setBucketId} allowClear />}
        </Field>
      </div>
    </Modal>
  );
}
